import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, message, Modal } from 'antd';
import { UserAddOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { createUserBySupplierAdmin, logSupplierAdminApproval } from '../../graphql/mutations';
import { getUserInfo, getLicenseFee } from '../../graphql/queries';
import { axiosRequest } from '../../utils/axiosRequest';
import styles from './SupplierAdminPortal.module.css';

export const CreateUserBySupplierAdmin = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [hasAcknowledgedCost, setHasAcknowledgedCost] = useState(false);
  const [licenseFee, setLicenseFee] = useState(null);

  useEffect(() => {
    // Get user info and license status
    Auth.currentSession()
      .then(async (session) => {
        const userEmail = session.getIdToken().payload.email;
        const headers = {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: session.getIdToken().getJwtToken(),
        };

        // Get vendor group number
        const userInfoBody = {
          query: getUserInfo,
          variables: { mail: userEmail },
        };
        const userInfoResult = await axiosRequest({ headers, body: userInfoBody, method: 'post' });
        const userData = userInfoResult.data?.data?.getUserInfo?.data?.[0];

        if (userData?.vendorGroupNumber) {
          const feeBody = {
            query: getLicenseFee,
            variables: {},
          };
          const feeResult = await axiosRequest({ headers, body: feeBody, method: 'post' });
          const fee = feeResult.data?.data?.getLicenseFee?.licenseFee;
          setLicenseFee(fee);
        }
      })
      .catch((err) => {
        console.error('Error getting license info:', err);
      });
  }, []);

  const getHeaders = async () => {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken();
    const jwt = idToken.getJwtToken();
    return {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: jwt,
    };
  };

  const handleAcknowledgeCost = async () => {
    try {
      const targetUserEmail = pendingFormData?.email?.toLowerCase();
      const headers = await getHeaders();
      const body = {
        query: logSupplierAdminApproval,
        variables: {
          userEmail: targetUserEmail,
        },
      };
      await axiosRequest({ headers, body, method: 'post' });
      setHasAcknowledgedCost(true);
      setShowCostModal(false);

      // Now submit the pending form
      if (pendingFormData) {
        await submitUser(pendingFormData);
      }
    } catch (error) {
      console.error('Error logging approval:', error);
      message.error('Failed to log approval. Please try again.');
    }
  };

  const submitUser = async (values) => {
    setLoading(true);
    setResponseMessage(null);
    setPendingFormData(null);

    try {
      const headers = await getHeaders();
      const body = {
        query: createUserBySupplierAdmin,
        variables: {
          email: values.email.toLowerCase(),
          firstName: values.firstName,
          lastName: values.lastName,
        },
      };
      const result = await axiosRequest({ headers, body, method: 'post' });

      const response = result.data?.data?.createUserBySupplierAdmin;

      if (response.success) {
        message.success(response.message);
        setResponseMessage({
          type: 'success',
          message: response.message,
          requiresApproval: response.requiresApproval,
        });

        // Reset form and navigate back after delay
        setTimeout(() => {
          navigate('/supplier-admin');
        }, 2000);
      } else {
        message.error(response.message);
        setResponseMessage({
          type: 'error',
          message: response.message,
        });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user. Please try again.');
      setResponseMessage({
        type: 'error',
        message: 'Failed to create user. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!hasAcknowledgedCost) {
      setPendingFormData(values);
      setShowCostModal(true);
    } else {
      await submitUser(values);
    }
  };

  return (
    <div className={styles['supplier-admin-form']}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/supplier-admin')}
        className={styles['mb-16']}
      >
        Back to Portal
      </Button>

      <Card
        title={
          <>
            <UserAddOutlined /> Create New User
          </>
        }
        className={styles['max-width-600']}
      >
        <Alert
          message="Approval Required"
          description="All new users will require approval from an internal administrator before they can access the system."
          type="info"
          showIcon
          className={styles['mb-24']}
        />

        {responseMessage && (
          <Alert
            message={responseMessage.type === 'success' ? 'Success' : 'Error'}
            description={responseMessage.message}
            type={responseMessage.type}
            showIcon
            closable
            onClose={() => setResponseMessage(null)}
            className={styles['mb-24']}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="John" />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Doe" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Create User
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        width={620}
        title="License Cost Acknowledgment"
        open={showCostModal}
        onOk={handleAcknowledgeCost}
        onCancel={() => {
          setShowCostModal(false);
          setPendingFormData(null);
        }}
        okText="I Acknowledge"
        cancelText="Cancel"
      >
        <Alert
          message="Additional Cost Notice (Non-Standard Supplier Program Vendors Only)"
          description={licenseFee !== null
            ? `Each active user license will incur an annual cost of $${licenseFee.toFixed(2)}. Please confirm you understand and approve this charge.`
            : "Each active user license will incur an annual cost. Please confirm you understand and approve this charge."
          }
          type="warning"
          showIcon
          className={styles['mb-16']}
        />
        <p>By clicking "I Acknowledge", you confirm that you understand and approve the annual licensing costs for new users. Vendors participating in the Standard Supplier Program will not incur this additional annual licensing fee.</p>
      </Modal>
    </div>
  );
};
